import React, { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Users, Calendar, CreditCard, Car, UserCheck, ExternalLink, Pencil, Trash2 } from "lucide-react";
import MyLinkCard from "../MyLinkCard";
import CreateTableModal from "../CreateTableModal";
import { confirmDeleteTable } from "../../lib/tables";
import { StatBox, DataTable, fmtDate, affiliateLabel } from "./AdminUI";

export function OverviewPanel({ user, stats }) {
  return (
    <>
      <div className="mb-6 max-w-2xl"><MyLinkCard code={user.referral_code} title="IL MIO LINK LISTA (PR)"/></div>
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <StatBox icon={Calendar} label="EVENTI" value={stats.events}/>
          <StatBox icon={Users} label="UTENTI" value={stats.users}/>
          <StatBox icon={UserCheck} label="LISTE" value={stats.list_signups}/>
          <StatBox icon={Users} label="TAVOLI" value={stats.tables}/>
          <StatBox icon={CreditCard} label="CAPARRE OK" value={stats.paid_deposits} alert/>
          <StatBox icon={Car} label="CARPOOL" value={stats.carpool_rides}/>
        </div>
      )}
    </>
  );
}

export function ListsPanel({ lists, clicks, events, users }) {
  const evTitle = (id) => events.find(e=>e.id===id)?.title || "?";
  return (
    <>
      <DataTable headers={["EVENTO","NOME","EMAIL","PERSONE","TELEFONO","PORTATO DA","DATA"]} rows={lists.map(l=>({
        key: l.id, cells: [evTitle(l.event_id), l.name, l.email || "-", l.people, l.phone || "-", affiliateLabel(l.referral_code, users), fmtDate(l.created_at)]
      }))}/>
      <div className="mt-8">
        <div className="label-mono text-neon mb-3">// CLICK SUL LINK LISTA ESTERNO</div>
        <DataTable headers={["EVENTO","PORTATO DA","CLICK"]} rows={clicks.map(c=>({
          key: `${c.event_id}-${c.referral_code || "direct"}`,
          cells: [evTitle(c.event_id), affiliateLabel(c.referral_code, users, "diretto"), c.clicks]
        }))}/>
      </div>
    </>
  );
}

const euro = (v) => v == null ? "-" : `€${v}`;

function TableRowActions({ t, onEdit, onChange }) {
  return (
    <div className="flex gap-3">
      <button onClick={()=>onEdit(t)} className="text-neutral-400 hover:text-neon transition-colors" aria-label="Modifica" data-testid={`admin-edit-table-${t.id}`}><Pencil className="w-4 h-4"/></button>
      <button onClick={async()=>{ if (await confirmDeleteTable(t)) onChange(); }} className="text-neutral-400 hover:text-alert transition-colors" aria-label="Elimina" data-testid={`admin-delete-table-${t.id}`}><Trash2 className="w-4 h-4"/></button>
    </div>
  );
}

export function TablesPanel({ tables, events, onChange }) {
  const [editing, setEditing] = useState(null);
  return (
    <>
      <DataTable headers={["EVENTO","TAVOLO","OWNER","MEMBRI","PAGATI","INGRESSO","TAVOLO €","TOTALE","CAPARRA","AZIONI"]} rows={tables.map(t=>({
        key: t.id, cells: [t.event_title, t.name, t.owner_name, `${t.members.length}/${t.capacity}`,
          `${t.members.filter(m=>m.paid).length}`, euro(t.entry_price), euro(t.table_price), euro(t.price_per_person),
          `€${t.deposit_per_person}`, <TableRowActions t={t} onEdit={setEditing} onChange={onChange}/>]
      }))}/>
      {editing && <CreateTableModal events={events} table={editing} onClose={()=>setEditing(null)} onCreated={()=>{setEditing(null); onChange();}}/>}
    </>
  );
}

export function PaymentsPanel({ payments, users, tables }) {
  return (
    <DataTable headers={["CHI","TAVOLO","PERSONE","NOTA","IMPORTO","INCASSA","STATUS","DATA"]} rows={payments.map(p=>({
      key: p.id, cells: [
        p.user_name || users.find(u=>u.id===p.user_id)?.name || "?",
        p.table_label || tables.find(t=>t.id===p.table_id)?.name || "?",
        p.people || 1, p.note || "-", `€${p.amount}`, p.payee_name || "AFTERLIST",
        p.payment_status === "paid" ? "✓ PAGATO" : p.payment_status.toUpperCase(),
        fmtDate(p.created_at)]
    }))}/>
  );
}

function RoleButton({ u, onChange }) {
  if (u.role === "admin") return "ADMIN";
  const next = u.role === "affiliate" ? "user" : "affiliate";
  const toggle = async () => {
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role: next });
      toast.success(next === "affiliate" ? `${u.name} è ora affiliato` : `${u.name} non è più affiliato`);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  return (
    <button onClick={toggle} data-testid={`role-toggle-${u.id}`}
      className={`px-2 py-1 border text-[10px] tracking-widest ${u.role === "affiliate" ? "border-neon text-neon" : "border-neutral-700 text-neutral-400 hover:text-white"}`}>
      {u.role === "affiliate" ? "AFFILIATO ✕" : "RENDI AFFILIATO"}
    </button>
  );
}

const stripeLabel = (u) => u.role !== "affiliate" ? "-" : u.stripe_charges_enabled ? "✓ COLLEGATO" : u.stripe_account_id ? "IN CORSO" : "NON COLLEGATO";

export function UsersPanel({ users, onChange }) {
  return (
    <DataTable headers={["NOME","EMAIL","RUOLO","STRIPE","REFERRAL","CREDITI","DATA"]} rows={users.map(u=>({
      key: u.id, cells: [u.name, u.email, <RoleButton u={u} onChange={onChange}/>, stripeLabel(u), u.referral_code, u.credits || 0, fmtDate(u.created_at)]
    }))}/>
  );
}

export function ScoutingPanel({ scouting }) {
  return (
    <div>
      <div className="card-dark p-6 mb-6 border-alert">
        <div className="label-mono text-alert mb-2">// TARGET_ACQUISITION</div>
        <p className="text-sm text-neutral-400">Link di ricerca mirata per trovare clubber attivi sui social. Clicca per aprire le pagine di ricerca con hashtag e tag locali già impostati.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {scouting.map((s,i)=>(
          <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer"
            className="card-dark p-4 flex items-center justify-between group hover:border-neon" data-testid={`scout-${i}`}>
            <div>
              <div className="label-mono text-neon mb-1">{s.platform}</div>
              <div className="font-display text-base">{s.label}</div>
            </div>
            <ExternalLink className="w-4 h-4 text-neutral-500 group-hover:text-neon"/>
          </a>
        ))}
      </div>
    </div>
  );
}
